import { chatApi } from "~/common/API/chatApi";
import { BASE_METHOD, fetchApi, FetchApiOrigin } from "~/common/API/baseApi";
import { toChatSocial } from "~/common/chatChannels";
import type {
  ISendMessage,
  ISendSystemMessage,
  IMessage,
} from "~/interfaces/chat";

interface Ticket {
  id: number;
  theme: string;
  status: string;
  userId: number;
  created: string;
  assigned?: number;
}

interface ChatResponse {
  messages: IMessage[];
  id: number;
}

interface TicketResponse {
  data: Ticket[];
}

interface TicketParams {
  "status[]"?: string[];
  userId?: number;
  limit?: number;
  offset?: number;
}

export default class ChatService {
  /**
   * Обрабатывает ошибки в сервисе
   * @param error - Ошибка для обработки
   * @param context - Контекст, в котором произошла ошибка
   * @throws Error с информацией об ошибке
   */
  static handleError = (error: unknown, context: string) => {
    console.error(`ChatService - ${context}:`, error);
    throw new Error(
      `Failed to ${context}: ${
        error instanceof Error ? error.message : String(error)
      }`
    );
  };

  /**
   * Канальный чат «канал + паблишер + рекламодатель»: возвращает существующий
   * или создаёт новый (вызов идемпотентный). С publicationId публикация
   * привязывается к чату.
   */
  static getOrCreateChannelChat = async (data: {
    placeId: number;
    social: string;
    publisherId: number;
    advertiserId: number;
    publicationId?: number;
  }) => {
    return await chatApi(
      "/api/v1/chat/channel",
      { method: BASE_METHOD.POST, body: { ...data, social: toChatSocial(data.social) } },
      "advertiser"
    );
  };

  /** Поиск канального чата без создания: чат или null. */
  static findChannelChat = async (params: {
    placeId: number;
    social: string;
    publisherId: number;
    advertiserId: number;
  }) => {
    return await chatApi(
      "/api/v1/chat/channel",
      { method: BASE_METHOD.GET, params: { ...params, social: toChatSocial(params.social) } },
      "advertiser"
    );
  };

  /**
   * Получает список чатов для текущего пользователя
   * @returns Promise со списком чатов
   * @throws Error если получение чатов не удалось
   */
  static getChats = async (params: {
    advertiserId?: number;
    publisherId?: number;
    userId: number;
  }) => {
    return await chatApi(
      "/api/v1/chat",
      {
        method: BASE_METHOD.GET,
        params: {
          limit: 1000,
          ...(params.advertiserId ? { advertiserId: params.advertiserId } : {}),
          ...(params.publisherId ? { publisherId: params.publisherId } : {}),
          ...(params.userId ? { userId: params.userId } : {}),
        },
      },
      "advertiser"
    );
  };

  /**
   * Получает публикацию по ID
   * @param id - ID публикации
   * @returns Promise с данными публикации
   * @throws Error если получение публикации не удалось
   */
  static getPublicationById = async (id: number) => {
    const endpoint = "/advert/publication";
    return await fetchApi({
      origin: FetchApiOrigin.LIGHT_API_ORIGIN,
      endpoint,
      method: BASE_METHOD.GET,
      params: { id },
    });
  };

  /**
   * Получает сообщения чата по ID
   * @param id - ID чата
   * @returns Promise со списком сообщений
   * @throws Error если получение сообщений не удалось
   */
  static getChatMessagesById = async (id: number) => {
    try {
      const response = await chatApi(
        `/api/v1/chat/${id}`,
        {
          method: BASE_METHOD.GET,
        },
        "advertiser"
      );
      return response;
    } catch (error) {
      console.log("ChatService - getChatMessagesById", error);
    }
  };

  /**
   * Получает сообщения чата по ID публикации
   * @param id - ID публикации
   * @returns Promise с объектом, содержащим сообщения и ID чата
   */
  static getChatMessagesByPublicationId = async (id: number) => {
    try {
      // Ищет и в канальных чатах (среди привязанных публикаций), и в старых.
      // Не найден — chatApi вернёт null
      const response = await chatApi(
        `/api/v1/chat/publication/${id}`,
        {
          method: BASE_METHOD.GET,
        },
        "advertiser"
      );
      return response;
    } catch (error) {
      console.log("ChatService - getChatMessagesByPublicationId", error);
    }
  };

  /**
   * Отправляет сообщение в чат
   * @param data - Данные сообщения
   * @returns Promise с результатом отправки
   * @throws Error если отправка не удалась
   */
  static sendMessage = async (data: ISendMessage) => {
    return await chatApi(
      "/api/v1/chat-message",
      {
        method: BASE_METHOD.POST,
        body: data,
      },
      "advertiser"
    );
  };

  /**
   * Отмечает все сообщения в чате как прочитанные
   * @param chatId - ID чата
   * @returns Promise с результатом отправки
   * @throws Error если отправка не удалась
   */
  static readAllMessages = async (chatId: number) => {
    return await chatApi(
      `/api/v1/chat/read/${chatId}`,
      {
        method: BASE_METHOD.PUT,
      },
      "advertiser"
    );
  };

  /**
   * Получает тикеты поддержки
   * @param payload - Параметры запроса
   * @returns Promise со списком тикетов
   */
  static getSupportTickets = async (payload?: { userId: number }) => {
    const endpoint = "/api/v1/ticket";
    return await chatApi(
      endpoint,
      {
        method: BASE_METHOD.GET,
        params: payload,
      },
      "advertiser"
    );
  };

  /**
   * Создает новый тикет поддержки
   * @param data - Данные для создания тикета
   * @returns Promise с созданным тикетом
   */
  static createSupportTicket = async (data: {
    userId: number;
    theme: string;
    userType: string;
  }) => {
    const endpoint = "/api/v1/ticket";
    return await chatApi(endpoint, {
      method: BASE_METHOD.POST,
      body: data,
    }, "advertiser");
  };

  /**
   * Получает историю сообщений пользователя
   * @returns Promise со списком сообщений
   */
  static getUserMessages = async () => {
    const endpoint = "/api/v1/ticket/conversation/history";
    return await chatApi(endpoint, {
      method: BASE_METHOD.GET,
      params: { limit: 200 },
    }, "advertiser");
  };

  /**
   * Отправляет системное сообщение
   * @param data - Данные системного сообщения
   * @returns Promise с результатом отправки
   */
  static sendSystemMessage = async (data: ISendSystemMessage) => {
    const endpoint = "/api/v1/ticket-message";
    return await chatApi(endpoint, {
      method: BASE_METHOD.POST,
      body: data,
    }, "advertiser");
  };

  /**
   * Получает тикеты для администратора
   * @param params - Параметры фильтрации
   * @returns Promise со списком тикетов
   */
  static getTicketsAdmin = async (
    params: TicketParams
  ): Promise<TicketResponse> => {
    const endpoint = "/api/v1/ticket/";
    return await chatApi(endpoint, {
      method: BASE_METHOD.GET,
      params,
    }, "advertiser");
  };

  /**
   * Получает сообщения тикета
   * @param id - ID тикета
   * @returns Promise с данными тикета
   */
  static getTicketMessages = async (id: number): Promise<Ticket> => {
    const endpoint = `/api/v1/ticket/${id}`;
    return await chatApi(endpoint, {
      method: BASE_METHOD.GET,
      params: { limit: 20 },
    }, "advertiser");
  };

  /**
   * Закрывает тикет
   * @param ticketId - ID тикета
   * @returns Promise с результатом операции
   */
  static closeTicket = async (ticketId: number) => {
    const endpoint = `/api/v1/ticket/${ticketId}`;
    return await chatApi(endpoint, {
      method: BASE_METHOD.PUT,
      body: {
        status: "CLOSE",
      },
    }, "advertiser");
  };

  /**
   * Открывает тикет
   * @param ticketId - ID тикета
   * @returns Promise с результатом операции
   */
  static openTicket = async (ticketId: number) => {
    const endpoint = `/api/v1/ticket/${ticketId}`;
    return await chatApi(endpoint, {
      method: BASE_METHOD.PUT,
      body: {
        status: "PROGRESS",
      },
    }, "advertiser");
  };

  static rateTicket = async (ticketId: number, rating: number) => {
    const endpoint = `/api/v1/ticket/${ticketId}`;
    return await chatApi(endpoint, {
      method: BASE_METHOD.PUT,
      body: { rating },
    }, "advertiser");
  };
}
